import { useState, useCallback } from 'react';

/**
 * Custom hook for handling API requests with loading and error states
 * @param {Function} apiFunction - The API function to call
 * @param {Object} options - Configuration options
 * @param {*} options.initialData - Initial data value
 * @param {Function} options.onSuccess - Callback for successful requests
 * @param {Function} options.onError - Callback for failed requests
 * @param {Function} options.transform - Function to transform the response data
 * @returns {Object} - { data, loading, error, execute, reset }
 */
export default (apiFunction, options = {}) => {
  const {
    initialData = null,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFunction(...args);
      
      setData(response);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response);
      }
      
      return response;
    } catch (err) {
      console.error('API request failed:', err);
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      
      // Call error callback if provided
      if (onError) {
        onError(err, errorMessage);
      }
      
    } finally {
      setLoading(false);
    }
  }, [apiFunction, onSuccess, onError]);

  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setLoading(false);
  }, [initialData]);

  return {
    data,
    loading,
    error,
    execute,
    reset
  };
}; 